import { ChangeEvent, FunctionComponent, useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { TabContext, TabList, TabPanel } from "@mui/lab";

import { Tab } from "@mui/material";

import { Stack } from "@mui/system";

import { useParams, useSearchParams } from "react-router-dom";

import { usePageTitle } from "@/utils/usePageTitle";

import { PermissionRoles } from "@/enum";

import { useHasPermissions } from "@/utils/useHasPermissions";

import { useGetCompanyForKpiQuery } from "@/pages/Private/redux/company/company.api";

import { CompanyType } from "../schema/company";
import { UsersTable } from "../../Users/components/UsersTable";
import { InviteUserForm } from "../../Users/components/InviteUserForm";

import { CompanyGeneral } from "./CompanyGeneralDetails";

type CompanyDetailsProps = {
	company?: CompanyType;
};

enum Tabs {
	GENERAL = "GENERAL",
	USERS = "USERS",
}

export const CompanyDetails: FunctionComponent<CompanyDetailsProps> = ({}) => {
	const { t } = useTranslation();
	const ts = (key: string) => t(`company.${key}`);

	const { id } = useParams();
	const { hasPermissions } = useHasPermissions();

	// A global admin browses other companies from the organisation list, so the
	// page has to say which one is on screen. Everyone else only ever sees their
	// own, where the name would be noise.
	const isGlobalAdmin = hasPermissions([PermissionRoles.GLOBAL_ADMIN]);
	const companyId = id ? +id : undefined;

	const { data: company } = useGetCompanyForKpiQuery(companyId ?? -1, {
		skip: !companyId || !isGlobalAdmin,
	});

	const [searchParams, setSearchParams] = useSearchParams();
	const validTab = (tab: string | null): Tabs =>
		Object.values(Tabs).includes(tab as Tabs) ? (tab as Tabs) : Tabs.GENERAL;

	const [currentTab, setCurrentTab] = useState<Tabs>(validTab(searchParams.get("tab")));

	usePageTitle(ts("general"));

	useEffect(() => {
		setCurrentTab(validTab(searchParams.get("tab")));
	}, [searchParams]);

	const tabs = [
		{ label: ts("tabs.general"), value: Tabs.GENERAL },
		{ label: ts("tabs.user"), value: Tabs.USERS },
	];

	const handleTabsChange = useCallback((_: ChangeEvent<object>, value: Tabs): void => {
		setSearchParams({ tab: value }, { replace: false });
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return (
		<div className="sm:block flex flex-col items-start justify-between mb-6">
			{isGlobalAdmin && company?.name && (
				<h2 className="mb-4 ml-4 text-lg font-semibold leading-7">{company.name}</h2>
			)}

			<TabContext value={currentTab}>
				<TabList aria-label="Company tabs" sx={{ ml: 2 }} onChange={handleTabsChange}>
					{tabs.map((tab, index) => (
						<Tab key={index} label={tab.label} value={tab.value} />
					))}
				</TabList>

				<TabPanel sx={{ p: 0 }} value={Tabs.GENERAL}>
					<CompanyGeneral />
				</TabPanel>

				<TabPanel sx={{ p: 0 }} value={Tabs.USERS}>
					<Stack spacing={4}>
						<InviteUserForm />
						<div className="flex flex-row justify-between w-full border-t px-4 py-6 border-divider">
							<div className="flex-grow w-[25%]">
								<h3 className="mb-3">{ts("company-members")}</h3>
								<p className="text-gray-700 lg:max-w-[650px] text-xs pr-5">
									{ts("company-member-description")}
								</p>
							</div>
							<div className="flex-grow w-[75%]">
								<UsersTable />
							</div>
						</div>
					</Stack>
				</TabPanel>
			</TabContext>
		</div>
	);
};
